import React, { useMemo, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { BoltArtifactRenderer, parseBoltArtifacts } from './BoltArtifactRenderer';
import { PlanCard, parsePlan, type PlanData } from './PlanCard';
import { AIChainOfThought } from '@/components/ai-elements';
import { type Message } from 'ai/react';
import {
  Folder,
  BookOpen,
  Search,
  Check,
  Wrench,
  ClipboardList,
  FlaskConical,
  Palette,
  Zap,
  Database,
  AlertTriangle,
  X,
  MousePointer2,
  Pencil,
  Image as ImageIcon,
  Code,
  type LucideIcon
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { PxMark } from '@/components/PxMark';
import { useSupabaseActionParser, SupabaseActionApproval, type SupabaseAction } from "@/components/integrations/SupabaseActionApproval";
import { SupabaseBatchApproval } from "@/components/integrations/SupabaseBatchApproval";

interface PixelMessageRendererProps {
  message: Message;
  projectId?: string;
  onPlanConfirm?: (plan: PlanData) => void;
  onPlanReject?: (plan: PlanData) => void;
  onActionResult?: (result: string) => void; // New callback for action feedback
  planStatus?: 'pending' | 'confirmed' | 'rejected';
}

function PixelMessageRendererBase({
  message,
  projectId,
  onPlanConfirm,
  onPlanReject,
  onActionResult, // Destructure new prop
  planStatus = 'pending'
}: PixelMessageRendererProps) {
  const { parseActions } = useSupabaseActionParser();
  const [activeSupabaseAction, setActiveSupabaseAction] = useState<SupabaseAction | null>(null);

  // Parse Supabase actions from content
  // CRITICAL SAFETY: Ensure content is always a string to prevent .trim() crash
  const content = String(message.content ?? '');
  const supabaseActions = useMemo(() => parseActions(content), [content, parseActions]);

  const handleSupabaseActionClick = (action: SupabaseAction) => {
    setActiveSupabaseAction(action);
  };

  const handleSupabaseActionClose = () => {
    setActiveSupabaseAction(null);
  };
  // State for image lightbox
  const [lightboxImage, setLightboxImage] = useState<string | null>(null);

  // Tool icon and label mapping — icônes lucide sobres, jamais d'emoji
  const getToolInfo = (toolName: string) => {
    const toolMap: Record<string, { icon: LucideIcon; label: string }> = {
      listFiles: { icon: Folder, label: 'Fichiers' },
      getFileContent: { icon: BookOpen, label: 'Lecture' },
      analyzeProject: { icon: Search, label: 'Analyse' },
      verifyProject: { icon: Check, label: 'Vérification' },
      repairProject: { icon: Wrench, label: 'Réparation' },
      planGeneration: { icon: ClipboardList, label: 'Plan' },
      validateCode: { icon: FlaskConical, label: 'Validation' },
      generateImage: { icon: Palette, label: 'Image' },
    };
    return toolMap[toolName] || { icon: Zap, label: toolName };
  };

  // Handle tool invocations display — rows mono 11, état en couleur sémantique
  const renderTools = () => {
    if (!message.toolInvocations?.length) return null;

    return <div className="flex flex-wrap gap-1.5 mb-3">
      {message.toolInvocations.map((tool: any) => {
        const { icon: ToolIcon, label } = getToolInfo(tool.toolName);
        const result = tool.state === 'result' ? tool.result : null;
        const isLoading = tool.state !== 'result';
        const isSuccess = result?.success !== false;

        // Special handling for generateImage with thumbnail
        if (tool.toolName === 'generateImage' && result?.success && result?.imageUrl) {
          return <div key={tool.toolCallId} className="flex items-center gap-2">
            <div className="flex items-center gap-1.5 border border-border bg-secondary px-2 py-1.5 font-mono text-[11px] text-muted-foreground">
              <ToolIcon className="h-3 w-3" />
              <span>{label}</span>
            </div>
            <button
              onClick={() => setLightboxImage(result.imageUrl)}
              className="group relative w-12 h-12 overflow-hidden border border-border hover:border-hairline-strong transition-colors cursor-pointer"
            >
              <img src={result.imageUrl} alt="Image générée" className="w-full h-full object-cover" loading="lazy" />
            </button>
          </div>;
        }

        // Standard tool badge
        return <div
          key={tool.toolCallId}
          className={cn(
            "flex items-center gap-1.5 border border-border bg-secondary px-2 py-1 font-mono text-[11px]",
            isLoading ? "text-faint" : isSuccess ? "text-muted-foreground" : "text-err"
          )}
        >
          {isLoading ? <PxMark size={10} state="thinking" /> : <ToolIcon className="h-3 w-3" />}
          <span>{isLoading ? `${label}…` : label}</span>
          {result?.totalCount !== undefined && <span className="ml-1 text-faint">({result.totalCount})</span>}
          {/* Show error if tool has error result */}
          {result?.success === false && result?.error && (
            <AlertTriangle className="ml-1 h-3 w-3 text-err" />
          )}
        </div>;
      })}
    </div>;
  };

  // Detect incomplete tools (started but never finished - indicates timeout)
  const hasIncompleteTools = !message.isStreaming &&
    message.toolInvocations?.some((t: any) => t.state === 'call');

  // Render error banner for incomplete tools
  const renderIncompleteToolsWarning = () => {
    if (!hasIncompleteTools) return null;
    const incompleteTools = message.toolInvocations?.filter((t: any) => t.state === 'call') || [];
    return (
      <div className="flex items-center gap-2 p-3 mb-3 text-sm border border-err/30 bg-err/10 text-err">
        <AlertTriangle className="h-4 w-4 shrink-0" />
        <span>
          {incompleteTools.length} outil(s) ont timeout: {incompleteTools.map((t: any) => t.toolName).join(', ')}.
          <button
            onClick={() => window.location.reload()}
            className="ml-2 underline underline-offset-2 hover:no-underline"
          >
            Réessayez
          </button>
        </span>
      </div>
    );
  };

  // Image lightbox modal — backdrop encre 40 %, cadre hairline-strong
  const renderLightbox = () => {
    if (!lightboxImage) return null;
    return (
      <div
        className="fixed inset-0 z-50 bg-foreground/40 flex items-center justify-center p-4"
        onClick={() => setLightboxImage(null)}
      >
        <div className="relative max-w-4xl max-h-[90vh]">
          <img
            src={lightboxImage}
            alt="Image générée"
            className="max-w-full max-h-[90vh] object-contain border border-hairline-strong bg-card"
          />
          <button
            onClick={() => setLightboxImage(null)}
            className="absolute top-2 right-2 h-8 w-8 border border-hairline-strong bg-card text-foreground flex items-center justify-center hover:bg-secondary transition-colors"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>
    );
  };



  // Debug: log removed

  // Extract thinking content, parse artifacts, AND detect plans
  const {
    artifacts,
    textContent,
    isStreaming,
    hasThinking,
    plan,
    textWithoutPlan
  } = useMemo(() => {
    // Check for thinking content
    const thinkingMatch = content.match(/<thinking>([\s\S]*?)<\/thinking>/i);
    const hasThinkingContent = !!thinkingMatch || content.includes('<thinking>');

    // Check for plan content
    const detectedPlan = parsePlan(content);
    let contentWithoutPlan = content;
    if (detectedPlan) {
      contentWithoutPlan = content.replace(/<pixelPlan>[\s\S]*?<\/pixelPlan>/i, '').trim();
    }

    const result = parseBoltArtifacts(contentWithoutPlan);
    // Detect if we're still streaming (has opening tag but no closing tag)
    const hasOpeningTag = /<boltArtifact[^>]*>/i.test(contentWithoutPlan);
    const hasClosingTag = /<\/boltArtifact>/i.test(contentWithoutPlan);
    const isStillStreaming = hasOpeningTag && !hasClosingTag;
    return {
      ...result,
      isStreaming: isStillStreaming,
      hasThinking: hasThinkingContent,
      plan: detectedPlan,
      textWithoutPlan: contentWithoutPlan
    };
  }, [content]);

  // Render thinking/reasoning section with collapsable component
  const renderThinkingBadge = () => {
    if (!hasThinking) return null;
    return (
      <AIChainOfThought
        title="Réflexion"
        defaultOpen={false}
        isLoading={isStreaming}
        className="mb-3"
      >
        <span>Analyse en cours…</span>
      </AIChainOfThought>
    );
  };

  // Custom Markdown Components — prose encre, puces carrées, liens brand-ink
  const markdownComponents: any = {
    h1: ({ children }: any) => (
      <h1 className="text-lg font-semibold text-foreground mb-3">
        {children}
      </h1>
    ),
    h2: ({ children }: any) => (
      <h2 className="text-base font-semibold text-foreground mb-2.5">
        {children}
      </h2>
    ),
    h3: ({ children }: any) => (
      <h3 className="text-sm font-medium text-foreground mb-2">
        {children}
      </h3>
    ),
    p: ({ children }: any) => (
      <p className="text-sm leading-relaxed mb-3 text-foreground">
        {children}
      </p>
    ),
    a: ({ children, href }: any) => (
      <a href={href} className="text-brand-ink underline underline-offset-2 hover:no-underline">
        {children}
      </a>
    ),
    ul: ({ children }: any) => (
      <ul className="space-y-1.5 mb-3">
        {children}
      </ul>
    ),
    ol: ({ children }: any) => (
      <ol className="space-y-1.5 mb-3 list-decimal list-inside">
        {children}
      </ol>
    ),
    li: ({ children }: any) => (
      <li className="text-sm flex items-start gap-2 text-foreground">
        <span className="mt-[7px] h-1 w-1 shrink-0 bg-faint"></span>
        <span className="flex-1">{children}</span>
      </li>
    ),
    strong: ({ children }: any) => (
      <strong className="font-semibold text-foreground">
        {children}
      </strong>
    ),
    code: ({ className, children, inline }: any) => {
      const isInline = inline || !className;
      const language = className?.replace('language-', '') || '';

      // Hide large code blocks (tsx, ts, jsx, js, javascript, typescript)
      const codeLanguages = ['tsx', 'ts', 'jsx', 'js', 'javascript', 'typescript'];
      if (!isInline && codeLanguages.includes(language)) {
        const codeContent = String(children).trim();
        const lineCount = codeContent.split('\n').length;

        // Show compact summary for code blocks
        return (
          <div className="my-2 border border-border bg-surface-3 overflow-hidden">
            <div className="px-3 py-2 flex items-center gap-2">
              <Code className="h-4 w-4 text-muted-foreground" />
              <span className="flex-1 font-mono text-xs text-foreground">
                Code {language.toUpperCase()}
              </span>
              <span className="font-mono text-[11px] text-faint">
                {lineCount} ligne{lineCount > 1 ? 's' : ''}
              </span>
            </div>
          </div>
        );
      }

      // Inline code
      if (isInline) {
        return (
          <code className="bg-secondary px-1.5 py-0.5 font-mono text-xs text-foreground">
            {children}
          </code>
        );
      }

      // Other block code (css, json, etc.)
      return (
        <code className="block overflow-x-auto border border-border bg-surface-3 px-3 py-2 font-mono text-xs text-foreground mb-3">
          {children}
        </code>
      );
    },
    blockquote: ({ children }: any) => (
      <blockquote className="pl-3 py-1 mb-3 border-l-2 border-hairline-strong text-muted-foreground">
        {children}
      </blockquote>
    ),
  };

  // If streaming (incomplete artifact), show v0-style progress with collapsable actions
  if (isStreaming) {
    // Extract title from the incomplete artifact if possible
    const titleMatch = content.match(/title=["']([^"']+)["']/i);
    const artifactTitle = titleMatch?.[1] || 'Code en cours de génération...';

    // Parse all file actions being generated
    const fileActionMatches = [...content.matchAll(/<boltAction\s+type="file"\s+filePath="([^"]+)"[^>]*>/gi)];
    const fileActions = fileActionMatches.map(m => m[1]);

    // Get last file being generated (incomplete)
    const lastFileMatch = content.match(/<boltAction\s+type="file"\s+filePath="([^"]+)"[^>]*>(?:(?!<\/boltAction>).)*$/si);
    const currentFile = lastFileMatch ? lastFileMatch[1] : null;

    // Get text before the boltArtifact tag, strip thinking tags
    let textBefore = content.replace(/<boltArtifact[\s\S]*$/i, '').trim();
    textBefore = textBefore.replace(/<thinking>[\s\S]*?<\/thinking>/gi, '');
    textBefore = textBefore.replace(/<\/?thinking>/gi, '');
    textBefore = textBefore.trim();

    return <div className="space-y-3 text-inherit">
      {renderThinkingBadge()}
      {textBefore && <div className="mb-2">
        <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>{textBefore}</ReactMarkdown>
      </div>}

      {/* Liste des actions en cours */}
      <div className="border border-border bg-card overflow-hidden">
        <div className="px-3 py-2 flex items-center justify-between border-b border-border">
          <span className="font-mono text-[11px] uppercase tracking-[0.1em] text-faint">Actions</span>
          <span className="bg-brand-wash px-1.5 py-0.5 font-mono text-[10px] uppercase tracking-wider text-brand-ink">
            En direct
          </span>
        </div>
        <div className="py-1">
          {fileActions.map((filePath, idx) => (
            <div
              key={idx}
              className="flex items-center gap-2 px-3 py-1.5 text-xs text-muted-foreground"
            >
              {filePath === currentFile ? (
                <PxMark size={12} state="generating" />
              ) : (
                <Check className="h-3 w-3 shrink-0 text-ok" />
              )}
              <span>{filePath === currentFile ? 'Création de' : 'Créé'}</span>
              <code className="bg-secondary px-1.5 py-0.5 font-mono text-[11px] text-foreground">
                {filePath}
              </code>
            </div>
          ))}
          {fileActions.length === 0 && (
            <div className="flex items-center gap-2 px-3 py-1.5 text-xs text-muted-foreground">
              <PxMark size={12} state="generating" />
              <span>Préparation de la génération…</span>
            </div>
          )}
        </div>
      </div>
    </div>;
  }

  // If message contains a plan, render PlanCard
  if (plan) {
    const planWithStatus = { ...plan, status: planStatus };
    return (
      <div className="space-y-3 text-inherit">
        {renderThinkingBadge()}
        {textContent && (
          <div className="mb-2">
            <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>
              {textContent.replace(/<pixelPlan>[\s\S]*?<\/pixelPlan>/gi, '').trim()}
            </ReactMarkdown>
          </div>
        )}
        <PlanCard
          plan={planWithStatus}
          onConfirm={() => onPlanConfirm?.(planWithStatus)}
          onReject={() => onPlanReject?.(planWithStatus)}
        />
      </div>
    );
  }

  // If message contains bolt artifacts, render them
  if (artifacts.length > 0) {
    return <>
      {renderLightbox()}
      <div className="space-y-3 text-inherit">
        {renderThinkingBadge()}
        {renderIncompleteToolsWarning()}
        {textContent && <div className="mb-2">
          <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>{textContent}</ReactMarkdown>
        </div>}
        {renderTools()}
        {artifacts.map((artifact, index) => <BoltArtifactRenderer key={artifact.id || index} artifact={artifact} rawContent={content} pixelProjectId={projectId} />)}
      </div>
    </>;
  }

  // Handle placeholder messages from progressive saving
  if (content === '[Génération en cours...]' || content.trim() === '[Génération en cours...]') {
    return (
      <div className="flex items-center gap-3 py-2">
        <PxMark size={16} state="generating" />
        <div className="flex flex-col gap-1">
          <span className="text-sm text-muted-foreground">Génération en cours…</span>
          <span className="text-xs text-faint">La réponse sera disponible dans quelques instants</span>
        </div>
      </div>
    );
  }
  // Handle special user message types with styled cards
  const isPlanModeRequest = content.startsWith('[PLAN_MODE]');
  const isExecutePlanRequest = content.startsWith('[EXECUTE_PLAN:');
  const isVisualModification = content.startsWith('[MODIFICATION VISUELLE]');
  const isElementSelected = content.startsWith('[ÉLÉMENT SÉLECTIONNÉ:');

  if (isElementSelected) {
    const elementMatch = content.match(/\[ÉLÉMENT SÉLECTIONNÉ:\s*([^\]]+)\]/);
    const elementInfo = elementMatch?.[1] || '';
    const userRequest = content.replace(/\[ÉLÉMENT SÉLECTIONNÉ:[^\]]+\]\s*/i, '').trim();
    return (
      <div className="flex items-start gap-3">
        <div className="h-8 w-8 shrink-0 border border-border bg-secondary flex items-center justify-center">
          <MousePointer2 className="h-4 w-4 text-foreground" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span className="font-mono text-[10px] uppercase tracking-wider text-faint">Élément ciblé</span>
            <code className="bg-secondary px-1.5 py-0.5 font-mono text-[11px] text-muted-foreground">{elementInfo.replace(/<|>/g, '')}</code>
          </div>
          <p className="text-sm text-foreground">{userRequest}</p>
        </div>
      </div>
    );
  }

  if (isPlanModeRequest) {
    const requestText = content.replace('[PLAN_MODE]', '').trim();
    return (
      <div className="flex items-start gap-3">
        <div className="h-8 w-8 shrink-0 border border-border bg-secondary flex items-center justify-center">
          <ClipboardList className="h-4 w-4 text-foreground" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span className="font-mono text-[10px] uppercase tracking-wider text-faint">Mode Plan</span>
            <span className="text-xs text-faint">· Proposition avant exécution</span>
          </div>
          <p className="text-sm text-foreground">{requestText}</p>
        </div>
      </div>
    );
  }

  if (isExecutePlanRequest) {
    const planIdMatch = content.match(/\[EXECUTE_PLAN:\s*([^\]]+)\]/);
    const planId = planIdMatch?.[1] || '';
    const planDescription = content.replace(/\[EXECUTE_PLAN:[^\]]+\]\s*/i, '').trim();
    return (
      <div className="flex items-start gap-3">
        <div className="h-8 w-8 shrink-0 border border-border bg-secondary flex items-center justify-center">
          <Check className="h-4 w-4 text-ok" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span className="font-mono text-[10px] uppercase tracking-wider text-faint">Exécution du plan</span>
          </div>
          <p className="text-sm text-foreground">{planDescription || 'Génération en cours…'}</p>
        </div>
      </div>
    );
  }

  if (isVisualModification) {
    // Parse the modification details
    const elementMatch = content.match(/Élément:\s*([^\n]+)/);
    const contentMatch = content.match(/Nouveau contenu:\s*"([^"]+)"/);
    const stylesMatch = content.match(/Nouveaux styles:\s*(\{[^}]+\})/);

    const elementName = elementMatch?.[1] || 'Élément';
    const newContent = contentMatch?.[1];
    const hasStyles = !!stylesMatch;

    return (
      <div className="flex items-start gap-3">
        <div className="h-8 w-8 shrink-0 border border-border bg-secondary flex items-center justify-center">
          <Pencil className="h-4 w-4 text-foreground" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span className="font-mono text-[10px] uppercase tracking-wider text-faint">Modification visuelle</span>
          </div>
          <p className="text-sm text-foreground mb-2">
            <code className="bg-secondary px-1.5 py-0.5 font-mono text-[11px] text-muted-foreground">{elementName}</code>
          </p>
          <div className="flex flex-wrap gap-2">
            {newContent && (
              <span className="border border-border bg-secondary px-2 py-0.5 font-mono text-[10px] uppercase tracking-wider text-muted-foreground">
                Contenu modifié
              </span>
            )}
            {hasStyles && (
              <span className="border border-border bg-secondary px-2 py-0.5 font-mono text-[10px] uppercase tracking-wider text-muted-foreground">
                Styles modifiés
              </span>
            )}
          </div>
        </div>
      </div>
    );
  }



  // Handle Supabase Actions
  if (supabaseActions.length > 0) {
    return (
      <div className="space-y-4">
        {supabaseActions.map((action, idx) => (
          <div key={action.id || idx} className="flex items-start gap-3">
            <div className="h-8 w-8 shrink-0 border border-border bg-secondary flex items-center justify-center">
              <Database className="h-4 w-4 text-foreground" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-2">
                <span className="font-mono text-[10px] uppercase tracking-wider text-faint">Action Supabase</span>
                <span className="font-mono text-[11px] text-faint">· {action.type.replace('supabase-', '')}</span>
              </div>

              <div className="border border-border bg-surface-3 p-3">
                <pre className="overflow-x-auto whitespace-pre-wrap font-mono text-xs text-muted-foreground mb-3 max-h-[150px]">
                  {action.content.slice(0, 200)}{action.content.length > 200 ? '...' : ''}
                </pre>
                <button
                  onClick={() => handleSupabaseActionClick(action)}
                  className="flex items-center gap-1.5 border border-border bg-card px-3 py-1.5 text-xs text-foreground hover:bg-secondary transition-colors"
                >
                  <Database className="w-3 h-3" />
                  Vérifier et exécuter
                </button>
              </div>
            </div>
          </div>
        ))}

        {/* Render standard content if any surrounds the actions */}
        <div className="mt-4">
          <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>
            {content.replace(/<boltAction\s+type="supabase-[^"]+"[^>]*>[\s\S]*?<\/boltAction>/g, '')}
          </ReactMarkdown>
        </div>

        {/* Approval Dialog */}
        {activeSupabaseAction && (
          <SupabaseActionApproval
            action={activeSupabaseAction}
            onApprove={(result) => {
              console.log('Action approved:', result);
              handleSupabaseActionClose();
              // Send result back to chat for AI to process
              if (onActionResult && result) {
                const resultStr = typeof result === 'string' ? result : JSON.stringify(result, null, 2);
                onActionResult(resultStr);
              }
            }}
            onReject={() => {
              handleSupabaseActionClose();
            }}
            onClose={handleSupabaseActionClose}
          />
        )}
      </div>
    );
  }

  // Handle Supabase Actions
  if (supabaseActions.length > 0) {
    return (
      <div className="space-y-4">
        {/* Batch Execute Button - only show if multiple actions */}
        {supabaseActions.length > 1 && (
          <div className="flex items-center justify-between border border-border bg-card p-3">
            <div className="flex items-center gap-2">
              <Database className="w-4 h-4 text-foreground" />
              <span className="text-sm font-medium text-foreground">
                {supabaseActions.length} actions Supabase en attente
              </span>
            </div>
            <button
              onClick={() => {
                // Execute first action, user will need to approve each
                handleSupabaseActionClick(supabaseActions[0]);
              }}
              className="flex items-center gap-1.5 bg-primary px-4 py-2 text-xs font-medium text-primary-foreground hover:bg-primary/90 transition-colors"
            >
              <Database className="w-3.5 h-3.5" />
              Commencer l'exécution ({supabaseActions.length})
            </button>
          </div>
        )}

        {supabaseActions.map((action, idx) => (
          <div key={action.id || idx} className="flex items-start gap-3">
            <div className="h-8 w-8 shrink-0 border border-border bg-secondary flex items-center justify-center">
              <Database className="h-4 w-4 text-foreground" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-2">
                <span className="font-mono text-[10px] uppercase tracking-wider text-faint">Action {idx + 1}/{supabaseActions.length}</span>
                <span className="font-mono text-[11px] text-faint">· {action.type.replace('supabase-', '')}</span>
              </div>

              <div className="border border-border bg-surface-3 p-3">
                <pre className="overflow-x-auto whitespace-pre-wrap font-mono text-xs text-muted-foreground mb-3 max-h-[150px]">
                  {action.content.slice(0, 200)}{action.content.length > 200 ? '...' : ''}
                </pre>
                <button
                  onClick={() => handleSupabaseActionClick(action)}
                  className="flex items-center gap-1.5 border border-border bg-card px-3 py-1.5 text-xs text-foreground hover:bg-secondary transition-colors"
                >
                  <Database className="w-3 h-3" />
                  Exécuter
                </button>
              </div>
            </div>
          </div>
        ))}

        {/* Render standard content if any surrounds the actions */}
        <div className="mt-4">
          <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>
            {content.replace(/<boltAction\s+type="supabase-[^"]+"[^>]*>[\s\S]*?<\/boltAction>/g, '')}
          </ReactMarkdown>
        </div>

        {/* Approval Dialog */}
        {activeSupabaseAction && (
          <SupabaseActionApproval
            action={activeSupabaseAction}
            onApprove={(result) => {
              console.log('Action approved:', result);
              handleSupabaseActionClose();
              // Send result back to chat for AI to process
              if (onActionResult && result) {
                const resultStr = typeof result === 'string' ? result : JSON.stringify(result, null, 2);
                onActionResult(resultStr);
              }
            }}
            onReject={() => {
              handleSupabaseActionClose();
            }}
            onClose={handleSupabaseActionClose}
          />
        )}
      </div>
    );
  }

  const isUserImage = content.startsWith('[IMAGE:');

  if (isUserImage) {
    // Format: [IMAGE:mime/type:base64_data] Optional text
    // We need to be careful with parsing as base64 can be long
    const endBracketIndex = content.indexOf(']');
    if (endBracketIndex !== -1) {
      const imageTag = content.substring(0, endBracketIndex + 1);
      const remainingText = content.substring(endBracketIndex + 1).trim();

      // Remove [IMAGE: and ]
      const inner = imageTag.slice(7, -1);
      // Split by first colon only to separate mime and base64
      const firstColonIndex = inner.indexOf(':');

      if (firstColonIndex !== -1) {
        const mimeType = inner.substring(0, firstColonIndex);
        const base64Data = inner.substring(firstColonIndex + 1);
        const imageUrl = `data:${mimeType};base64,${base64Data}`;

        return (
          <div className="space-y-4">
            <div className="flex items-start gap-3">
              <div className="h-8 w-8 shrink-0 border border-border bg-secondary flex items-center justify-center">
                <ImageIcon className="h-4 w-4 text-foreground" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-2">
                  <span className="font-mono text-[10px] uppercase tracking-wider text-faint">Image envoyée</span>
                </div>

                <div className="relative group overflow-hidden border border-border bg-surface-3 max-w-sm">
                  <img
                    src={imageUrl}
                    alt="Image envoyée"
                    className="w-full h-auto object-cover max-h-[300px]"
                  />
                </div>
              </div>
            </div>

            {remainingText && (
              <div className="pl-11">
                <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>{remainingText}</ReactMarkdown>
              </div>
            )}
          </div>
        );
      }
    }
  }

  const isImageAsset = content.startsWith('[IMAGE_ASSET:');

  if (isImageAsset) {
    const assetUrlRaw = content.replace('[IMAGE_ASSET:', '').replace(']', '').trim();
    // Handle both raw URLs and JSON if we decide to pass more metadata later
    let assetUrl = assetUrlRaw;
    let assetLabel = 'Maquette de référence';

    // Basic check if it's a URL or base64?
    // We'll rely on HomeContent sending a valid URL or base64 data URI

    return (
      <div className="flex items-start gap-3">
        <div className="h-8 w-8 shrink-0 border border-border bg-secondary flex items-center justify-center">
          <ImageIcon className="h-4 w-4 text-foreground" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-2">
            <span className="font-mono text-[10px] uppercase tracking-wider text-faint">Référence visuelle</span>
          </div>

          <div className="relative group overflow-hidden border border-border bg-surface-3 max-w-sm">
            <img
              src={assetUrl}
              alt="Maquette de référence"
              className="w-full h-auto object-cover max-h-[300px]"
            />
            <div className="absolute inset-x-0 bottom-0 p-2 bg-foreground/75 opacity-0 group-hover:opacity-100 transition-opacity">
              <p className="font-mono text-[11px] text-background cursor-default">{assetLabel}</p>
            </div>
          </div>

          <div className="mt-2 text-xs text-faint">
            L'IA utilisera cette image comme référence pour la structure et le style.
          </div>
        </div>
      </div>
    );
  }

  // Standard markdown display — la réponse finale de l'agent vit sur le wash
  // rose (sans bordure), son lead en Instrument Serif italic.
  const isFinalAssistantResponse = message.role === 'assistant';
  return <>
    {renderLightbox()}
    <div className="space-y-4 text-inherit">
      {renderThinkingBadge()}
      {renderTools()}
      <div className={cn(
        isFinalAssistantResponse && [
          "bg-brand-wash p-4",
          "[&>p:first-of-type]:font-display [&>p:first-of-type]:italic",
          "[&>p:first-of-type]:text-[17px] [&>p:first-of-type]:leading-snug",
        ]
      )}>
        <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>{content}</ReactMarkdown>
      </div>
    </div>
  </>;
}

export const PixelMessageRenderer = React.memo(PixelMessageRendererBase, (prevProps, nextProps) => {
  // Custom comparison to ensure we only re-render when strictly necessary
  // Message content and ID are key
  return prevProps.message.id === nextProps.message.id &&
    prevProps.message.content === nextProps.message.content &&
    prevProps.planStatus === nextProps.planStatus &&
    prevProps.message.toolInvocations === nextProps.message.toolInvocations;
});
